/**
 * @fileoverview Opponent-adjusted passing and rushing prediction calculations.
 *
 * Builds four unit ratings from completed games before a selected week: passing/rushing
 * offense and passing/rushing defense. Rates combine season and recent opportunities,
 * account for opponent quality, and restrain small samples toward fixed league references.
 * Actual pass/rush usage then weights each offense-versus-defense matchup.
 *
 * Preparation validates schedule identities and weekly categories for the whole league.
 * Finalization converts the ratings to expected points, applies venue/rest context, floors
 * scores at zero, and derives win probabilities and a team pick. These helpers perform no
 * fetching or writes. Model coefficients come from configuration, not version comparisons.
 *
 * @module pass-rush-prediction-helpers
 * @see docs/predictive-analysis/model-v1.5.0-design-spec.md
 */

import {
  AccumulatedTeamCategoriesByGameStats,
  IGamePrediction,
  IMatchup,
  IPredictionModelConfig,
  PassRushGameObservation,
  PassRushLeagueReference,
  PassRushMetric,
  PassRushPredictionConfig,
  PredictionUnitBreakdown,
  PreparedPassRushPrediction,
  SeasonWeekID,
} from '../types';
import { breakSeasonWeekID, toSeasonTeamAbrvID, toTeamAbrvID } from './id-helpers';

// Rate definitions pair each offensive metric with its defensive counterpart.
const METRICS: Record<string, PassRushMetric> = {
  passOffYards: {
    numerator: 'gross',
    denominator: 'ATT',
    direction: 1,
    counterpart: 'passDefYards',
  },
  passOffTD: {
    numerator: 'TD_PASS_F',
    denominator: 'ATT',
    direction: 1,
    counterpart: 'passDefTD',
    event: true,
  },
  passOffINT: {
    numerator: 'INT_THR',
    denominator: 'ATT',
    direction: -1,
    counterpart: 'passDefINT',
    event: true,
  },
  passOffSack: {
    numerator: 'SACKED',
    denominator: 'dropbacks',
    direction: -1,
    counterpart: 'passDefSack',
  },
  passDefYards: {
    numerator: 'grossAgainst',
    denominator: 'attemptsAgainst',
    direction: -1,
    counterpart: 'passOffYards',
  },
  passDefTD: {
    numerator: 'passTDAgainst',
    denominator: 'attemptsAgainst',
    direction: -1,
    counterpart: 'passOffTD',
    event: true,
  },
  passDefINT: {
    numerator: 'interceptions',
    denominator: 'attemptsAgainst',
    direction: 1,
    counterpart: 'passOffINT',
    event: true,
  },
  passDefSack: {
    numerator: 'sacksMade',
    denominator: 'dropbacksAgainst',
    direction: 1,
    counterpart: 'passOffSack',
  },
  rushOffYards: {
    numerator: 'RUSH_YDS',
    denominator: 'RUSH_ATT',
    direction: 1,
    counterpart: 'rushDefYards',
  },
  rushOffTD: {
    numerator: 'TD_RUSH_F',
    denominator: 'RUSH_ATT',
    direction: 1,
    counterpart: 'rushDefTD',
    event: true,
  },
  rushDefYards: {
    numerator: 'rushAgainst',
    denominator: 'rushAttemptsAgainst',
    direction: -1,
    counterpart: 'rushOffYards',
  },
  rushDefTD: {
    numerator: 'rushTDAgainst',
    denominator: 'rushAttemptsAgainst',
    direction: -1,
    counterpart: 'rushOffTD',
    event: true,
  },
  ballSecurity: {
    numerator: 'FUM_LOST',
    denominator: 'games',
    direction: -1,
    event: true,
  },
};
// Required stored categories; derived defensive opportunities use the matched opponent record.
const RAW = [
  'PASS_YDS',
  'SACK_YDS',
  'ATT',
  'SACKED',
  'TD_PASS_F',
  'INT_THR',
  'RUSH_YDS',
  'RUSH_ATT',
  'TD_RUSH_F',
  'FUM_LOST',
  'PTS_FOR',
];
/**
 * Sum one validated category or opportunity count across played-game observations.
 *
 * @param {PassRushGameObservation[]} rows - Played games in the selected window
 * @param {string} key - Stored or derived numeric field to total
 *
 * @return {number} Total, or zero for an empty window
 */
const sum = (rows: PassRushGameObservation[], key: string) => rows.reduce((total, row) => total + row.values[key], 0);
/**
 * Calculate the equal-weight mean used for league rates and population variance.
 *
 * @param {number[]} values - Eligible team values
 *
 * @return {number} Arithmetic mean, or zero when no values are available
 */
const mean = (values: number[]) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0);
/**
 * Bound a signed value symmetrically without changing its direction.
 *
 * @param {number} n - Value to bound
 * @param {number} limit - Nonnegative absolute limit
 *
 * @return {number} Value constrained to [-limit, limit]
 */
const clamp = (n: number, limit: number) => Math.max(-limit, Math.min(limit, n));

/**
 * Blend season and recent rates using summed numerators and opportunities in each window.
 * Rows must be chronological and contain only played games. A recent window with no
 * opportunities falls back to the season rate; no season opportunities means no estimate.
 *
 * @param {PassRushGameObservation[]} rows - Validated chronological observations
 * @param {string} numerator - Numeric category to aggregate
 * @param {string} denominator - Opportunity field corresponding to the category
 * @param {PassRushPredictionConfig} c - Recent-window length and blend weight
 *
 * @return {number | undefined} Blended rate, or undefined when no opportunities exist
 */
const blendedRate = (
  rows: PassRushGameObservation[],
  numerator: string,
  denominator: string,
  c: PassRushPredictionConfig,
): number | undefined => {
  const opportunities = sum(rows, denominator);
  if (!opportunities) return undefined;
  const season = sum(rows, numerator) / opportunities;
  const recent = rows.slice(-c.recentGames);
  const recentOpportunities = sum(recent, denominator);
  const recentRate = recentOpportunities ? sum(recent, numerator) / recentOpportunities : season;
  return (1 - c.recentWeight) * season + c.recentWeight * recentRate;
};

/**
 * Prepare league-wide pass/rush ratings using only completed games before the target week.
 *
 * Reconciles schedule and weekly identities, excludes byes, and derives defensive rates
 * from each matching opponent's offensive record. Opponent estimates exclude meetings
 * with the assessed team. Fixed league means/spreads keep normalization from undoing
 * sample restraint. Week 1 produces neutral ratings and the configured scoring baseline.
 *
 * @param {SeasonWeekID} seasonWeekID - Target prediction week; its outcomes are excluded
 * @param {IMatchup[]} schedule - Season schedule containing team/game/week identities
 * @param {AccumulatedTeamCategoriesByGameStats} weekly - Stored per-team weekly category arrays
 * @param {IPredictionModelConfig} config - Model containing passRushModel coefficients
 *
 * @return {PreparedPassRushPrediction} Shared week inputs with ratings and scoring baseline
 * @throws {Error} Missing settings, invalid week, incomplete games, mismatched identities or invalid categories
 */
export const preparePassRushPrediction = (
  seasonWeekID: SeasonWeekID,
  schedule: IMatchup[],
  weekly: AccumulatedTeamCategoriesByGameStats,
  config: IPredictionModelConfig,
): PreparedPassRushPrediction => {
  const c = config.passRushModel;
  if (!c) throw new Error('Expected pass/rush prediction settings');
  const { yearID, numericWeekID: targetWeek } = breakSeasonWeekID(seasonWeekID);
  if (!Number.isInteger(targetWeek) || targetWeek < 1 || targetWeek > 18) throw new Error('Invalid target week');
  const games = schedule.filter((g) => g.seasonWeekID.startsWith(`${yearID}-`));
  const histories: Record<string, PassRushGameObservation[]> = {};
  games.forEach((g) => {
    histories[g.teamIDHome] = [];
    histories[g.teamIDAway] = [];
  });
  const seen = new Set<string>();
  for (const g of games) {
    const week = breakSeasonWeekID(g.seasonWeekID).numericWeekID;
    if (week >= targetWeek) continue;
    if (!['Final', 'Final/OT'].includes(g.gameStatus)) throw new Error(`Missing completed game ${g.gameID}`);
    for (const [team, opponent] of [
      [g.teamIDHome, g.teamIDAway],
      [g.teamIDAway, g.teamIDHome],
    ]) {
      const identity = `${team}:${week}`;
      if (seen.has(identity)) throw new Error(`Duplicate team/week ${identity}`);
      seen.add(identity);
      /**
       * Read and validate a team's categories at the current scheduled game's week index.
       *
       * @param {string} id - Team ID whose weekly record is required
       *
       * @return {Record<string, number>} Validated numeric categories for this game
       * @throws {Error} Missing record, mismatched identity or invalid required category
       */
      const read = (id: string): Record<string, number> => {
        const key = toSeasonTeamAbrvID(yearID, id);
        const data = weekly[key];
        if (!data) throw new Error(`Missing weekly data for ${key}`);
        if (data.gameID?.[week - 1] !== g.gameID || data.seasonWeekID?.[week - 1] !== g.seasonWeekID) {
          throw new Error(`Schedule/weekly identity mismatch for ${key} Week${week}`);
        }
        const values: Record<string, number> = {};
        for (const field of RAW) {
          const raw = (data as unknown as Record<string, unknown>)[field];
          const item = Array.isArray(raw) ? raw[week - 1] : undefined;
          if (typeof item !== 'string' || !item.trim() || !Number.isFinite(Number(item))) {
            throw new Error(`Missing/invalid ${field} for ${key} Week${week}`);
          }
          values[field] = Number(item);
          if (values[field] < 0 && !['PASS_YDS', 'RUSH_YDS'].includes(field))
            throw new Error(`Negative ${field} for ${key}`);
        }
        return values;
      };
      const v = read(team),
        o = read(opponent);
      const dropbacks = v.ATT + v.SACKED;
      const plays = dropbacks + v.RUSH_ATT;
      if (!plays) throw new Error(`No offensive opportunities for ${team} in ${g.gameID}`);
      histories[team].push({
        gameID: g.gameID,
        week,
        opponent,
        values: {
          ...v,
          gross: v.PASS_YDS + v.SACK_YDS,
          dropbacks,
          plays,
          games: 1,
          grossAgainst: o.PASS_YDS + o.SACK_YDS,
          attemptsAgainst: o.ATT,
          passTDAgainst: o.TD_PASS_F,
          interceptions: o.INT_THR,
          sacksMade: o.SACKED,
          dropbacksAgainst: o.ATT + o.SACKED,
          rushAgainst: o.RUSH_YDS,
          rushAttemptsAgainst: o.RUSH_ATT,
          rushTDAgainst: o.TD_RUSH_F,
        },
      });
    }
  }
  // Reconcile in both directions: a stored played game must not disappear from
  // the schedule and silently be treated as a bye. Bye IDs contain no matchup.
  for (const team of Object.keys(histories)) {
    const data = weekly[toSeasonTeamAbrvID(yearID, team)];
    for (let week = 1; week < targetWeek; week++) {
      if (data?.gameID?.[week - 1]?.includes('@') && !seen.has(`${team}:${week}`)) {
        throw new Error(`Weekly game missing from schedule for ${team} Week${week}`);
      }
    }
  }
  Object.values(histories).forEach((rows) => rows.sort((a, b) => a.week - b.week));
  const allRows = Object.values(histories).flat();
  const references: Record<string, PassRushLeagueReference> = {};
  for (const [key, m] of Object.entries(METRICS)) {
    const rates = Object.values(histories)
      .map((rows) => blendedRate(rows, m.numerator, m.denominator, c))
      .filter((v): v is number => v !== undefined);
    const average = mean(rates);
    references[key] = {
      mean: average,
      spread: rates.length >= 2 ? Math.sqrt(mean(rates.map((v) => (v - average) ** 2))) : 0,
      prior:
        (m.event ? c.eventPriorGames : c.efficiencyPriorGames) *
        (allRows.length ? sum(allRows, m.denominator) / allRows.length : 0),
    };
  }
  const teams: Record<string, PredictionUnitBreakdown> = {};
  for (const [team, rows] of Object.entries(histories)) {
    const components: Record<string, number> = {};
    for (const [key, m] of Object.entries(METRICS)) {
      const ref = references[key];
      const rate = blendedRate(rows, m.numerator, m.denominator, c);
      const opportunities = sum(rows, m.denominator);
      if (rate === undefined || ref.spread === 0) {
        components[key] = 0;
        continue;
      }
      const corrected = rows.map((row) => {
        let adjustment = 0;
        if (m.counterpart) {
          const opposite = METRICS[m.counterpart],
            oppRef = references[m.counterpart];
          // Exclude all meetings with this team, including a repeated divisional opponent.
          const others = histories[row.opponent].filter((other) => other.opponent !== team);
          const oppRate = blendedRate(others, opposite.numerator, opposite.denominator, c);
          const oppN = sum(others, opposite.denominator);
          if (oppRate !== undefined && oppN > 0) {
            const evidence = oppN / (oppN + oppRef.prior);
            adjustment = c.opponentCorrection * evidence * (oppRef.mean - oppRate);
          }
        }
        return {
          ...row,
          values: {
            ...row.values,
            corrected: row.values[m.numerator] + adjustment * row.values[m.denominator],
          },
        };
      });
      const adjusted = blendedRate(corrected, 'corrected', m.denominator, c)!;
      const evidence = opportunities / (opportunities + ref.prior);
      // Fixed unrestrained reference spread prevents normalization undoing shrinkage.
      components[key] = clamp((m.direction * (adjusted - ref.mean) * evidence) / ref.spread, c.componentCap);
      if (!Number.isFinite(components[key])) throw new Error(`Invalid normalized component ${team}/${key}`);
    }
    /**
     * Combine normalized passing components for the requested side of the ball.
     *
     * @param {string} prefix - passOff or passDef component prefix
     *
     * @return {number} Weighted passing unit rating
     */
    const pass = (prefix: string) =>
      components[`${prefix}Yards`] * c.passingWeights.yards +
      components[`${prefix}TD`] * c.passingWeights.touchdowns +
      components[`${prefix}INT`] * c.passingWeights.interceptions +
      components[`${prefix}Sack`] * c.passingWeights.sacks;
    /**
     * Combine normalized rushing components for the requested side of the ball.
     *
     * @param {string} prefix - rushOff or rushDef component prefix
     *
     * @return {number} Weighted rushing unit rating
     */
    const rush = (prefix: string) =>
      components[`${prefix}Yards`] * c.rushingWeights.yards + components[`${prefix}TD`] * c.rushingWeights.touchdowns;
    const passShare = blendedRate(rows, 'dropbacks', 'plays', c) ?? 0.5;
    teams[team] = {
      passingOffense: pass('passOff'),
      passingDefense: pass('passDef'),
      rushingOffense: rush('rushOff'),
      rushingDefense: rush('rushDef'),
      passShare,
      rushShare: 1 - passShare,
      ballSecurityContribution: c.ballSecurityWeight * components.ballSecurity,
    };
  }
  return {
    seasonWeekID,
    teams,
    baseline: allRows.length ? sum(allRows, 'PTS_FOR') / allRows.length : c.weekOnePoints,
  };
};

/**
 * Resolve an exactly even prediction with a repeatable team selection.
 * Uses FNV-1a over gameID|version: the lower unsigned half selects home, the upper half away.
 * This does not change scores or probabilities and does not use runtime randomness.
 *
 * @param {string} gameID - Canonical game identifier
 * @param {string} version - Model version included in the deterministic seed
 *
 * @return {boolean} True selects the home team; false selects the away team
 */
export const coinFlipHome = (gameID: string, version: string): boolean => {
  const seed = `${gameID}|${version}`;
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i++) hash = Math.imul(hash ^ seed.charCodeAt(i), 16777619) >>> 0;
  return hash < 0x80000000;
};

/**
 * Calculate a game's expected scores and winner from prepared pass/rush matchup ratings.
 *
 * Converts usage-weighted matchup advantages into points, splits the venue/rest margin
 * between teams, and floors each score at zero. Unrounded final scores determine the
 * signed margin and logistic probability. Exactly 50/50 uses a repeatable coin flip.
 * The result preserves the existing absolute predictedMargin contract and includes a
 * compact derived breakdown; it does not copy raw weekly inputs into the prediction.
 *
 * @param {IMatchup} game - Scheduled matchup and optional venue/rest context
 * @param {PreparedPassRushPrediction} prepared - League inputs prepared for this game's target week
 * @param {IPredictionModelConfig} config - Same model settings used during preparation
 * @param {string} generatedAt - Caller-supplied ISO timestamp for the resulting record
 *
 * @return {IGamePrediction} Full prediction with unrounded scores, probabilities and explanation
 * @throws {Error} Prepared cutoff differs from the game week or either team's ratings are missing
 */
export const calculatePassRushPrediction = (
  game: IMatchup,
  prepared: PreparedPassRushPrediction,
  config: IPredictionModelConfig,
  generatedAt: string,
): IGamePrediction => {
  const c = config.passRushModel!;
  if (game.seasonWeekID !== prepared.seasonWeekID) throw new Error('Prediction cutoff does not match game');
  const home = prepared.teams[game.teamIDHome],
    away = prepared.teams[game.teamIDAway];
  if (!home || !away) throw new Error(`Missing matchup teams for ${game.gameID}`);
  /**
   * Weight offense-versus-defense advantages by the offense's observed play usage.
   *
   * @param {PredictionUnitBreakdown} a - Attacking team ratings, usage and ball security
   * @param {PredictionUnitBreakdown} d - Opposing team's defensive ratings
   *
   * @return {number} Matchup assessment in rating units, before conversion to points
   */
  const assessment = (a: PredictionUnitBreakdown, d: PredictionUnitBreakdown) =>
    (1 - c.ballSecurityWeight) *
      (a.passShare * (a.passingOffense - d.passingDefense) + a.rushShare * (a.rushingOffense - d.rushingDefense)) +
    a.ballSecurityContribution;
  const h = prepared.baseline + c.pointsPerRating * assessment(home, away);
  const a = prepared.baseline + c.pointsPerRating * assessment(away, home);
  const week = breakSeasonWeekID(game.seasonWeekID).numericWeekID;
  const context = game.gameContext;
  const notes: string[] = [];
  const venue = context?.venue?.type;
  const venueAdvantage = venue === 'Home' ? c.homeFieldMargin : 0;
  if (!context || !venue) notes.push('Venue context unavailable; no home-field adjustment.');
  let restAdvantage = 0;
  if (week > 1) {
    if (Number.isFinite(context?.homeTeamRestDays) && Number.isFinite(context?.awayTeamRestDays)) {
      restAdvantage = c.restPointsPerDay * clamp(context!.homeTeamRestDays - context!.awayTeamRestDays, c.restDayCap);
    } else notes.push('Rest context unavailable; no rest adjustment.');
  }
  const contextMargin = venueAdvantage + restAdvantage;
  const homeScore = Math.max(0, h + contextMargin / 2),
    awayScore = Math.max(0, a - contextMargin / 2);
  const margin = homeScore - awayScore;
  const homeProbability = 100 / (1 + Math.exp(-margin / c.probabilityScale));
  const favored = Math.max(homeProbability, 100 - homeProbability);
  const coinFlip = homeProbability === 50;
  const homePick = coinFlip ? coinFlipHome(game.gameID, config.version) : homeProbability > 50;
  if (coinFlip) notes.push('Even matchup; pick decided by coin flip.');
  if (week === 1) notes.push('Week 1: neutral team strength, configured scoring prior, no rest adjustment.');
  for (const [label, offense, defense] of [
    ['Home', home, away],
    ['Away', away, home],
  ] as const) {
    notes.push(
      `${label}: pass advantage ${(offense.passingOffense - defense.passingDefense).toFixed(2)}, ` +
        `rush advantage ${(offense.rushingOffense - defense.rushingDefense).toFixed(2)}; ` +
        `pass usage ${(100 * offense.passShare).toFixed(1)}%.`,
    );
  }
  notes.push(
    `Statistical home margin ${(h - a).toFixed(2)}; ` +
      `venue ${venueAdvantage.toFixed(2)}; rest ${restAdvantage.toFixed(2)} points.`,
  );
  return {
    gameID: game.gameID,
    seasonWeekID: game.seasonWeekID,
    seasonID: breakSeasonWeekID(game.seasonWeekID).yearID,
    modelVersion: config.version,
    pickType: 'team',
    modelConfig: config,
    generatedAt,
    coinFlip,
    homeTeam: {
      teamAbrvID: toTeamAbrvID(game.teamIDHome),
      predictedScore: homeScore,
      winProbability: homeProbability,
    },
    awayTeam: {
      teamAbrvID: toTeamAbrvID(game.teamIDAway),
      predictedScore: awayScore,
      winProbability: 100 - homeProbability,
    },
    predictedWinnerTeamAbrvID: toTeamAbrvID(homePick ? game.teamIDHome : game.teamIDAway),
    predictedMargin: Math.abs(margin),
    confidence: favored >= c.highProbability ? 'high' : favored >= c.mediumProbability ? 'medium' : 'low',
    keyFactors: notes,
    matchupAdvantages: [],
    contextFactors: {
      venueAdvantage,
      restAdvantage,
      divisionRivalry: context?.isDivisionGame ?? false,
      primeTime: context?.primeTime ?? false,
    },
    passRushBreakdown: { home, away, statisticalMargin: h - a },
  };
};
